import React, { useState, useEffect, useCallback, useRef } from "react";
import { TextField, Box, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, ListItemIcon } from "@material-ui/core";
import ImageIcon from "@material-ui/icons/Image";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import GridOnIcon from "@material-ui/icons/GridOn";

const OPTIONS_LAYOUTS = [
	{ id: "row", label: "Fila", Icon: ViewListIcon, desc: "Vertical" },
	{ id: "columns", label: "Columnas", Icon: ViewColumnIcon, desc: "Horizontal" },
	{ id: "grid", label: "Cuadrícula", Icon: GridOnIcon, desc: "Grid" },
];

const QUESTION_TYPES = [
	{ id: "trueFalse", label: "Verdadero / Falso", Icon: ToggleOnIcon },
	{ id: "multipleChoice", label: "Opciones", Icon: FormatListBulletedIcon },
	{ id: "imageChoice", label: "Imágenes", Icon: ImageIcon },
	{ id: "optionsWithImages", label: "Opciones con imágenes", Icon: PhotoLibraryIcon },
];

function emptyQuestion(type = "multipleChoice") {
	const base = { optionsLayout: "row", gridColumns: 2 };
	if (type === "trueFalse") {
		return { ...base, type: "trueFalse", text: "", options: [{ text: "Verdadero", imageUrl: "", correct: false }, { text: "Falso", imageUrl: "", correct: false }], explanation: "" };
	}
	return { ...base, type: type || "multipleChoice", text: "", options: [{ text: "", imageUrl: "", correct: false }], explanation: "" };
}

function normalizeQuestion(q) {
	const t = q?.type || "multipleChoice";
	const layout = q?.optionsLayout || "row";
	const gridCols = Math.min(6, Math.max(1, Number(q?.gridColumns) || 2));
	if (t === "trueFalse") {
		return {
			type: "trueFalse",
			text: q?.text || "",
			optionsLayout: layout,
			gridColumns: gridCols,
			options: [
				{ text: "Verdadero", imageUrl: "", correct: (q?.options || [])[0]?.correct ?? false },
				{ text: "Falso", imageUrl: "", correct: (q?.options || [])[1]?.correct ?? false },
			],
			explanation: q?.explanation || "",
		};
	}
	return {
		type: t,
		text: q?.text || "",
		optionsLayout: layout,
		gridColumns: gridCols,
		options: (q?.options || []).map((o) => ({ text: o?.text || "", imageUrl: o?.imageUrl || "", correct: !!o?.correct })),
		explanation: q?.explanation || "",
	};
}

export default function InlineQuizEditor({ quiz, onSave, updateQuiz, userMedia = [] }) {
	const [question, setQuestion] = useState(() => normalizeQuestion(quiz?.questions?.[0]));
	const [pointsCorrect, setPointsCorrect] = useState(quiz?.pointsCorrect ?? 10);
	const [pointsIncorrect, setPointsIncorrect] = useState(quiz?.pointsIncorrect ?? -5);
	const [allowMultipleAttempts, setAllowMultipleAttempts] = useState(!!quiz?.allowMultipleAttempts);
	const saveTimeoutRef = useRef(null);

	const quizId = quiz?._id;
	const quizQuestions = quiz?.questions;
	const quizPointsCorrect = quiz?.pointsCorrect;
	const quizPointsIncorrect = quiz?.pointsIncorrect;
	const quizAllowMultiple = quiz?.allowMultipleAttempts;
	useEffect(() => {
		if (quiz) {
			setQuestion(normalizeQuestion(quiz.questions?.[0]));
			setPointsCorrect(quiz.pointsCorrect ?? 10);
			setPointsIncorrect(quiz.pointsIncorrect ?? -5);
			setAllowMultipleAttempts(!!quiz.allowMultipleAttempts);
		}
	}, [quiz, quizId, quizQuestions, quizPointsCorrect, quizPointsIncorrect, quizAllowMultiple]);

	const debouncedSave = useCallback(() => {
		if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
		saveTimeoutRef.current = setTimeout(async () => {
			if (!quiz?._id || !updateQuiz) return;
			try {
				const payload = {
					fullName: (question.text || "Pregunta").slice(0, 80),
					questions: [question],
					pointsCorrect: Number(pointsCorrect) || 0,
					pointsIncorrect: Number(pointsIncorrect) || 0,
					allowMultipleAttempts,
				};
				const res = await updateQuiz(quiz._id, payload);
				if (onSave) onSave(res.data);
			} catch (e) {
				// Silently fail, user can retry
			}
			saveTimeoutRef.current = null;
		}, 600);
	}, [quiz?._id, question, pointsCorrect, pointsIncorrect, allowMultipleAttempts, updateQuiz, onSave]);

	useEffect(() => {
		if (quiz?._id) debouncedSave();
		return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
	}, [question, pointsCorrect, pointsIncorrect, allowMultipleAttempts, debouncedSave]);

	const setQ = (field, value) => setQuestion((q) => ({ ...q, [field]: value }));
	const changeType = (newType) => {
		const prev = question;
		const q = emptyQuestion(newType);
		if (newType === "trueFalse") setQuestion(q);
		else setQuestion({
			...q,
			text: prev.text,
			explanation: prev.explanation,
			options: prev.options?.length ? prev.options.map((o) => ({ ...o, imageUrl: o.imageUrl || "" })) : q.options,
		});
	};

	if (!quiz) return null;

	return (
		<Box display="flex" flexDirection="column" style={{ gap: 24 }}>
			{/* Tipo */}
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Tipo</Box>
				<FormControl variant="outlined" fullWidth size="medium">
					<InputLabel>Tipo de pregunta</InputLabel>
					<Select
						value={question.type || "multipleChoice"}
						onChange={(e) => changeType(e.target.value)}
						label="Tipo de pregunta"
						style={{ fontSize: 15 }}
					>
						{QUESTION_TYPES.map(({ id, label, Icon }) => (
							<MenuItem key={id} value={id}>
								<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Disposición */}
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Disposición</Box>
				<Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
					<FormControl variant="outlined" size="medium" style={{ minWidth: 180, flex: 1 }}>
						<InputLabel>Disposición de opciones</InputLabel>
						<Select
							value={question.optionsLayout || "row"}
							onChange={(e) => setQ("optionsLayout", e.target.value)}
							label="Disposición de opciones"
							style={{ fontSize: 15 }}
						>
							{OPTIONS_LAYOUTS.map(({ id, label, Icon }) => (
								<MenuItem key={id} value={id}>
									<ListItemIcon><Icon fontSize="small" style={{ minWidth: 36 }} /></ListItemIcon>
									{label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{question.optionsLayout === "grid" && (
						<FormControl variant="outlined" size="medium" style={{ minWidth: 100 }}>
							<InputLabel>Columnas</InputLabel>
							<Select
								value={question.gridColumns || 2}
								onChange={(e) => setQ("gridColumns", Number(e.target.value))}
								label="Columnas"
								style={{ fontSize: 15 }}
							>
								{[2, 3, 4, 5, 6].map((n) => (
									<MenuItem key={n} value={n}>{n}</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</Box>
			</Box>

			{/* Explicación */}
			<Box>
				<TextField
					label="Explicación"
					fullWidth
					multiline
					rows={3}
					value={question.explanation}
					onChange={(e) => setQ("explanation", e.target.value)}
					variant="outlined"
					placeholder="Opcional: se muestra tras responder"
					InputProps={{ style: { fontSize: 15 } }}
					InputLabelProps={{ style: { fontSize: 14 } }}
				/>
			</Box>

			{/* Puntos */}
			<Box>
				<Box fontSize={13} fontWeight={600} color="#334155" mb={1.5}>Puntos</Box>
				<Box display="flex" style={{ gap: 12 }}>
					<Box
						flex={1}
						p={2}
						borderRadius={10}
						style={{
							background: "linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)",
							border: "1px solid rgba(34,197,94,0.3)",
						}}
					>
						<Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
							<CheckCircleIcon style={{ fontSize: 20, color: "#16a34a" }} />
							<Box fontSize={12} fontWeight={600} color="#16a34a">Acierto</Box>
						</Box>
						<TextField
							type="number"
							value={pointsCorrect}
							onChange={(e) => setPointsCorrect(Number(e.target.value) || 0)}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ min: 0, style: { fontSize: 18, fontWeight: 600 } }}
							InputProps={{ style: { backgroundColor: "#fff" } }}
						/>
					</Box>
					<Box
						flex={1}
						p={2}
						borderRadius={10}
						style={{
							background: "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.04) 100%)",
							border: "1px solid rgba(239,68,68,0.3)",
						}}
					>
						<Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
							<CancelIcon style={{ fontSize: 20, color: "#dc2626" }} />
							<Box fontSize={12} fontWeight={600} color="#dc2626">Fallo</Box>
						</Box>
						<TextField
							type="number"
							value={pointsIncorrect}
							onChange={(e) => setPointsIncorrect(Number(e.target.value) || 0)}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ min: -100, style: { fontSize: 18, fontWeight: 600 } }}
							InputProps={{ style: { backgroundColor: "#fff" } }}
						/>
					</Box>
				</Box>
				<FormControlLabel
					control={<Switch checked={allowMultipleAttempts} onChange={(e) => setAllowMultipleAttempts(e.target.checked)} color="primary" />}
					label="Varios intentos"
					style={{ marginTop: 12, marginLeft: 0, fontSize: 15 }}
				/>
			</Box>
		</Box>
	);
}
